import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FilePreviewModal from '../../common/FilePreviewModal';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import { 
  Alert, 
  CircularProgress, 
  TextField, 
  InputAdornment, 
  IconButton, 
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';

// API URL setup - get backend URL from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// For debugging
console.log('API Configuration:', {
  API_URL,
  API_BASE,
  NODE_ENV: process.env.NODE_ENV
});

// Helper function to make sure URLs are always relative paths
const formatAsRelativePath = (url: string): string => {
  if (!url) return '';
  
  // If it's already a relative path starting with /, return as is
  if (url.startsWith('/')) return url;
  
  // If it's a full URL, extract just the path portion
  if (url.includes('://')) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch (e) {
      console.error('Invalid URL:', url);
      // If URL parsing fails, try to extract the path after the domain
      const parts = url.split('/');
      if (parts.length > 2) {
        // Remove protocol and domain parts
        return '/' + parts.slice(3).join('/');
      }
      return url;
    }
  }
  
  // If it doesn't start with / and isn't a full URL, add the leading /
  return `/${url}`;
};

const formatTotalSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 2)} ${sizes[i]}`;
};

// Interface for file metadata
interface FileItem {
  fieldName: string;
  fieldLabel: string;
  filePath: string;
  filename: string;
  downloadUrl: string;
  previewUrl: string;
}

// Interface for application with file count
interface ApplicationWithFileCount {
  _id: string;
  applicationId: string;
  email: string;
  formData?: {fullName?: string; phoneNumber?: string; mobileNumber?: string; personalMobile?: string; nameInBlocks?: string; dealershipType?: string; partnerDirectorDetails?: any[]};
  fullName?: string;
  fileCount: number;
  folderSize?: number;
  folderSizeFormatted?: string;
  applicantCount?: number;
  phoneNumber?: string;
  createdAt?: string;
  status?: string;
  ddReviewStatus?: string | null;
  ddReviewMode?: string | null;
  ddImageUrl?: string | null;
}

interface SearchFilters {
  searchTerm: string;
  field: 'all' | 'applicationId' | 'name' | 'email' | 'phone';
}

// Styled components for the file management UI
const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #1e3a8a;
  display: inline-block;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SummaryPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #e0f2fe 0%, #bfdbfe 35%, #c4b5fd 100%);
  color: #1e293b;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
`;

const SummaryIcon = styled.span`
  font-size: 0.95rem;
`;

const SummaryLabel = styled.span`
  opacity: 0.85;
`;

const SummaryValue = styled.span`
  font-weight: 700;
`;

// Removed any potential subtitle that showed fixed count (20)


const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 5px;
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 600px;
  gap: 10px;
`;

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

// Base file card component for consistency
const FileCard = styled(motion.div)`
  background-color: white;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  height: 100%;
  position: relative;
`;

// Clickable version of FileCard with enhanced hover effects - styled as folder
const ClickableFileCard = styled(FileCard)`
  cursor: pointer;
  background: linear-gradient(135deg, #FFD54F 0%, #FFCA28 100%);
  border: none;
  border-radius: 4px 16px 8px 8px;
  position: relative;
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: -12px;
    left: 0;
    width: 45%;
    height: 16px;
    background: linear-gradient(135deg, #FFA726 0%, #FF9800 100%);
    border-radius: 6px 12px 0 0;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(255, 193, 7, 0.4);
    background: linear-gradient(135deg, #FFEB3B 0%, #FFD54F 100%);
  }
`;

const FileLabel = styled.div`
  font-weight: 600;
  color: #1a2533;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
`;

const FileName = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ButtonGroup = styled.div`
  display: flex;
  margin-top: auto;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled(motion.button)`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f1f5f9;
    color: #4c6ef5;
    border-color: #4c6ef5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 1.25rem;
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const ReplaceFileButton = styled.div`
  background-color: #4c6ef5;
  color: white;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #3b5bdb;
  }
  
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #94a3b8;
  }
`;

const ZipDownloadButton = styled(motion.button)`
  background-color: #4c6ef5;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #3b5bdb;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: #64748b;
`;

const NoFiles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: #64748b;
  text-align: center;
`;

// File Preview Modal Styled Components
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
`;

const PreviewModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease;
`;

const PreviewModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 16px;
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  z-index: 10001;
  animation: ${slideIn} 0.3s ease;
  overflow: hidden;
`;

const PreviewModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const PreviewModalTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
`;

const PreviewModalCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  svg {
    font-size: 1.25rem;
  }
`;

const PreviewModalBody = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #f8fafc;
  min-height: 300px;
  max-height: calc(90vh - 120px);
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 160px);
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PreviewPdf = styled.iframe`
  width: 80vw;
  height: calc(90vh - 140px);
  border: none;
  border-radius: 8px;
`;

const PreviewUnsupported = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;

  svg {
    font-size: 4rem;
    color: #94a3b8;
    margin-bottom: 16px;
  }

  h4 {
    margin: 0 0 8px 0;
    color: #334155;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const PreviewModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: white;
`;

const PreviewDownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  svg {
    font-size: 1.1rem;
  }
`;

// API utility function for file upload
const uploadFormFileAdmin = async (applicationId: string, file: File, fieldName: string, onProgress?: (event: any) => void) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await axios.post(
      `${API_BASE}/api/admin/applications/${applicationId}/upload-file/${fieldName}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        onUploadProgress: onProgress,
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Interface for application details
interface ApplicationDetails {
  applicationId: string;
  applicantName: string;
  applicationType: string;
  totalPartners: number;
  partnerDirectorDetails: Array<{ nameInBlocks?: string; name?: string; id?: string }>;
   ddReviewStatus?: string | null;
   ddReviewMode?: string | null;
   ddImageUrl?: string | null;
}

// Main component for managing application files (admin version)
const AdminApplicationFiles: React.FC<{ applicationId?: string }> = ({ applicationId }) => {
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>(applicationId || '');
  const [applications, setApplications] = useState<ApplicationWithFileCount[]>([]);
  const [allApplications, setAllApplications] = useState<ApplicationWithFileCount[]>([]); // Store all applications for filtering
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails>({
    applicationId: '',
    applicantName: '',
    applicationType: 'Individual',
    totalPartners: 1,
    partnerDirectorDetails: []
  });
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    searchTerm: '',
    field: 'all'
  });
  const [showOnlyWithFiles, setShowOnlyWithFiles] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalApplications, setTotalApplications] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const ITEMS_PER_PAGE = 10; // Updated from 100 to 10 for better pagination experience
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'idle' | 'uploading' | 'success' | 'error'>>({});
  const [uploadMessages, setUploadMessages] = useState<Record<string, string>>({});
  const [ddActionLoading, setDdActionLoading] = useState<boolean>(false);
  const [ddActionError, setDdActionError] = useState<string | null>(null);
  const [ddActionSuccess, setDdActionSuccess] = useState<string | null>(null);
  
  // Preview modal state
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    url: string;
    filename: string;
    fileType: string;
    fieldName: string;
  }>({ isOpen: false, url: '', filename: '', fileType: '', fieldName: '' });
  
  // Cleanup modal state
  const [cleanupModal, setCleanupModal] = useState<{
    isOpen: boolean;
    orphanedFiles: string[];
    loading: boolean;
    deleting: boolean;
    error: string | null;
    success: string | null;
  }>({ isOpen: false, orphanedFiles: [], loading: false, deleting: false, error: null, success: null });
  
  // Handle finding orphaned files
  const handleFindOrphanedFiles = async () => {
    if (!selectedApplicationId) return;
    
    setCleanupModal(prev => ({ ...prev, isOpen: true, loading: true, error: null, success: null }));
    
    try {
      const response = await axios.get(
        `${API_BASE}/api/admin/applications/${selectedApplicationId}/orphaned-files`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setCleanupModal(prev => ({
          ...prev,
          loading: false,
          orphanedFiles: response.data.orphanedFiles || []
        }));
      } else {
        setCleanupModal(prev => ({
          ...prev,
          loading: false,
          error: response.data.message || 'Failed to find orphaned files'
        }));
      }
    } catch (error: any) {
      setCleanupModal(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Error scanning for orphaned files'
      }));
    }
  };
  
  // Handle deleting orphaned files
  const handleDeleteOrphanedFiles = async () => {
    if (!selectedApplicationId || cleanupModal.orphanedFiles.length === 0) return;
    
    setCleanupModal(prev => ({ ...prev, deleting: true, error: null }));
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/admin/applications/${selectedApplicationId}/cleanup-files`,
        { files: cleanupModal.orphanedFiles },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setCleanupModal(prev => ({
          ...prev,
          deleting: false,
          orphanedFiles: [],
          success: `Successfully deleted ${response.data.deletedCount} orphaned files`
        }));
      } else {
        setCleanupModal(prev => ({
          ...prev,
          deleting: false,
          error: response.data.message || 'Failed to delete files'
        }));
      }
    } catch (error: any) {
      setCleanupModal(prev => ({
        ...prev,
        deleting: false,
        error: error.response?.data?.message || 'Error deleting orphaned files'
      }));
    }
  };
  
  // Close cleanup modal
  const closeCleanupModal = () => {
    setCleanupModal({ isOpen: false, orphanedFiles: [], loading: false, deleting: false, error: null, success: null });
  };

  // Helper function to determine MIME type for preview
  const getFileMimeType = (filename: string): string => {
    const ext = filename.toLowerCase().split('.').pop() || '';
    const mimeMap: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
      gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml',
      pdf: 'application/pdf',
    };
    return mimeMap[ext] || 'application/octet-stream';
  };

  // Handle opening file preview modal
  const handlePreviewFile = (file: FileItem) => {
    const path = file.previewUrl.includes('/uploads/')
      ? `/uploads/${file.previewUrl.split('/uploads/')[1]}`
      : file.previewUrl;
    const url = `${API_BASE}${path}`;
    const fileType = getFileMimeType(file.filename);

    setPreviewModal({
      isOpen: true,
      url,
      filename: file.filename,
      fileType,
      fieldName: file.fieldName,
    });
  };

  // Handle closing preview modal
  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false, url: '', filename: '', fileType: '', fieldName: '' });
  };

  // Handle save success from FilePreviewModal — refresh the files list
  const handlePreviewSaveSuccess = (newFilePath: string) => {
    // Refresh files list to show the updated image
    if (selectedApplicationId) {
      fetchFiles();
    }
  };

  // Handle download from preview modal
  const handleDownloadFromPreview = () => {
    if (previewModal.url) {
      const link = document.createElement('a');
      link.href = previewModal.url;
      link.download = previewModal.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Helper function to get filtered applications based on current filters
  const getFilteredApplications = useCallback(() => {
    if (allApplications.length === 0) return [];
    
    let filteredApps = [...allApplications];
    
    // Filter by file count if checkbox is checked
    if (showOnlyWithFiles) {
      filteredApps = filteredApps.filter(app => app.fileCount > 0);
    }
    
    // Apply search filter if needed
    if (searchFilters.searchTerm.trim()) {
      const searchTerm = searchFilters.searchTerm.trim().toLowerCase();
      
      filteredApps = filteredApps.filter(app => {
        // Use the complete search implementation that handles all fields correctly
        if (searchFilters.field === 'all') {
          return (
            app.applicationId.toLowerCase().includes(searchTerm) ||
            ((app.fullName || app.formData?.fullName || app.formData?.nameInBlocks) || '').toLowerCase().includes(searchTerm) ||
            app.email.toLowerCase().includes(searchTerm) ||
            (app.formData?.phoneNumber || '').toLowerCase().includes(searchTerm)
          );
        } else if (searchFilters.field === 'applicationId') {
          return app.applicationId.toLowerCase().includes(searchTerm);
        } else if (searchFilters.field === 'name') {
          return ((app.fullName || app.formData?.fullName || app.formData?.nameInBlocks) || '').toLowerCase().includes(searchTerm);
        } else if (searchFilters.field === 'email') {
          return app.email.toLowerCase().includes(searchTerm);
        } else if (searchFilters.field === 'phone') {
          return (app.formData?.phoneNumber || '').toLowerCase().includes(searchTerm);
        }
        return false;
      });
    }
    
    return filteredApps;
  }, [allApplications, showOnlyWithFiles, searchFilters]);
  
  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(prev => ({ ...prev, searchTerm: e.target.value }));
    setPage(1); // Reset page on new search
  };

  // Handle checkbox change for applications with files
  const handleFileFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyWithFiles(e.target.checked);
    setPage(1); // Reset page on filter change
  };

  // Effect to update displayed applications when filters or page change
  useEffect(() => {
    if (allApplications.length === 0) return;

    const filteredApps = getFilteredApplications();
    
    const newTotalPages = Math.max(1, Math.ceil(filteredApps.length / ITEMS_PER_PAGE));
    setTotalPages(newTotalPages);

    // If current page is out of bounds after filtering, reset to page 1
    if (page > newTotalPages) {
      setPage(1);
      return; // Effect will re-run with the correct page
    }
    
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setApplications(filteredApps.slice(startIndex, endIndex));
  }, [getFilteredApplications, page]);
  
  // Handle pagination change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Get current page items - now simply returns applications since they're already paginated
  const getCurrentPageItems = useCallback(() => {
    return applications;
  }, [applications]);

  // Fetch applications with optimized loading strategy
  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      axios.defaults.withCredentials = true;
      
      // First, fetch ALL applications to get the accurate total count (should be 100)
      // Force fetchAll=true to ensure we get the complete count for pagination
      const response = await axios.get(
        `${API_BASE}/api/admin/applications?fetchAll=true`
      );
      
      if (response.data.success) {
        // Get the full list of applications
        const allApplicationsList = response.data.applications || [];
        const total = allApplicationsList.length;
        
        // Set the total counts for pagination with correct calculation
        // Make sure we're using the full count from the API (should be 100, not 10)
        setTotalApplications(total); // This will be used in the title
        const calculatedPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
        setTotalPages(calculatedPages);
        
        // Store the complete list of applications
        setAllApplications(allApplicationsList);
        
        // Initialize applications with zero file counts to show them immediately
        const initialApplications = allApplicationsList.map((app: any) => ({
          ...app,
          fileCount: 0
        }));
        
        // For initial display, we'll show first page (we'll update after fetching counts)
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        
        // Only filter by showOnlyWithFiles if we already have file counts
        // Otherwise, show the first page and we'll update after getting counts
        setApplications(initialApplications.slice(startIndex, endIndex));
        
        // Then fetch file counts in batch to avoid multiple API calls
        const appIds = allApplicationsList.map((app: any) => app.applicationId).join(',');
        
        // Only if we have applications
        if (appIds.length > 0) {
          try {
            const countsResponse = await axios.get(
              `${API_BASE}/api/admin/applications/files/count-batch?ids=${appIds}`,
              { withCredentials: true }
            );
            
            if (countsResponse.data.success && countsResponse.data.counts) {
              // Update applications with file counts
              const fileCounts = countsResponse.data.counts;
              
              // Also fetch metadata (folder size, applicant count, phone number)
              let metadata: Record<string, any> = {};
              try {
                const metadataResponse = await axios.get(
                  `${API_BASE}/api/admin/applications/metadata-batch?ids=${appIds}`,
                  { withCredentials: true }
                );
                if (metadataResponse.data.success) {
                  metadata = metadataResponse.data.metadata || {};
                }
              } catch (metaErr) {
                console.warn('Error fetching metadata batch:', metaErr);
              }
              
              const updatedApplications = initialApplications.map((app: any) => ({
                ...app,
                fileCount: fileCounts[app.applicationId] || 0,
                folderSize: metadata[app.applicationId]?.folderSize || 0,
                folderSizeFormatted: metadata[app.applicationId]?.folderSizeFormatted || '0 Bytes',
                applicantCount: metadata[app.applicationId]?.applicantCount || 1,
                phoneNumber: metadata[app.applicationId]?.phoneNumber || '',
                ddReviewStatus: metadata[app.applicationId]?.ddReviewStatus ?? null,
                ddReviewMode: metadata[app.applicationId]?.ddReviewMode ?? null,
                ddImageUrl: metadata[app.applicationId]?.ddImageUrl ?? null
              }));
              
              // Update both lists with file counts and metadata
              setAllApplications(updatedApplications);
              
              // Update displayed applications for current page
              const startIdx = (page - 1) * ITEMS_PER_PAGE;
              const endIdx = startIdx + ITEMS_PER_PAGE;
              setApplications(updatedApplications.slice(startIdx, endIdx));
            }
          } catch (countError) {
            console.warn('Error fetching batch file counts, falling back to individual requests');
            
            // Fallback: Update file counts one by one (but still in parallel)
            const updatedApplications = await Promise.all(
              initialApplications.map(async (app: any) => {
                try {
                  const fileResponse = await axios.get(
                    `${API_BASE}/api/admin/applications/${app.applicationId}/files/count`,
                    { withCredentials: true }
                  );
                  
                  return {
                    ...app,
                    fileCount: fileResponse.data.count || 0
                  };
                } catch (error) {
                  return app; // Keep existing count
                }
              })
            );
            
            // Update both lists with file counts (same logic as above)
            setAllApplications(prev => {
              const newList = [...prev];
              const startIndex = (page - 1) * ITEMS_PER_PAGE;
              updatedApplications.forEach((app, idx) => {
                if (startIndex + idx < newList.length) {
                  newList[startIndex + idx] = app;
                }
              });
              return newList;
            });
            
            setApplications(prev => {
              const newList = [...prev];
              const startIndex = (page - 1) * ITEMS_PER_PAGE;
              updatedApplications.forEach((app, idx) => {
                if (startIndex + idx < newList.length) {
                  newList[startIndex + idx] = app;
                }
              });
              return newList;
            });
          }
        }
      } else {
        setError(response.data.message || 'Failed to fetch applications.');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to fetch applications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Download all files as ZIP
  const handleDownloadAllAsZip = async () => {
    console.log('[AdminFiles] handleDownloadAllAsZip called for:', selectedApplicationId);
    if (!selectedApplicationId) return;
    
    try {
      setLoading(true);
      
      const downloadUrl = `${API_BASE}/api/admin/applications/${selectedApplicationId}/files/download-all`;
      console.log('[AdminFiles] Downloading from URL:', downloadUrl);
      
      const response = await axios.get(
        downloadUrl, 
        { responseType: 'blob', withCredentials: true }
      );
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `application-${selectedApplicationId}-files.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('[AdminFiles] Error downloading ZIP:', error);
      console.error('[AdminFiles] Error response:', error.response?.data);
      console.error('[AdminFiles] Error status:', error.response?.status);
      setError('Failed to download files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle application selection
  const handleApplicationSelect = (appId: string) => {
    console.log('Application selected:', appId);
    setSelectedApplicationId(appId);
    setFiles([]);
    
    // Don't use setTimeout as it can cause race conditions
    // Instead, use the updated state in a useEffect
  };

  // Fetch files function that can be called to refresh data
  const fetchFiles = async () => {
    if (!selectedApplicationId) {
      setFiles([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Log the API call for debugging
      console.log(`Fetching files for application: ${selectedApplicationId}`);
      console.log(`API URL: ${API_BASE}/api/admin/applications/${selectedApplicationId}/files`);
      
      // Fetch files and application details in parallel
      const [filesResponse, detailsResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/applications/${selectedApplicationId}/files`),
        axios.get(`${API_BASE}/api/admin/applications/${selectedApplicationId}`).catch(() => null)
      ]);
      
      console.log('Files API response:', filesResponse.data);
      
      if (filesResponse.data.success) {
        // Fix URLs to ensure they are properly formatted for the current domain
        const correctedFiles = filesResponse.data.files.map((file: FileItem) => ({
          ...file,
          // Format URLs to be relative paths (without domain) so they work across environments
          previewUrl: formatAsRelativePath(file.previewUrl),
          downloadUrl: formatAsRelativePath(file.downloadUrl)
        }));
        
        console.log('Processed files:', correctedFiles);
        setFiles(correctedFiles);
      } else {
        console.error('API returned success: false', filesResponse.data);
        setError('Failed to fetch files: ' + (filesResponse.data.message || 'Unknown error'));
      }

      // Extract application details
      if (detailsResponse?.data?.success && detailsResponse.data.application) {
        const app = detailsResponse.data.application;
        const formData = app.formData || {};
        const partners = formData.partnerDirectorDetails || [];
        // Total partners includes main applicant (Partner #1) + additional partners
        const totalPartnerCount = formData.totalNumberOfPartners || formData.numberOfPartners || (partners.length + 1);
        // dealershipType is the actual field name for application type (Individual, Partnership, Pvt Ltd, etc.)
        const appType = formData.dealershipType || formData.typeOfApplicant || formData.applicationType || 'Individual';
        const ddReview = app.metadata?.ddReview || {};
        setApplicationDetails({
          applicationId: app.applicationId || selectedApplicationId,
          applicantName: formData.nameInBlocks || formData.fullName || formData.name || 'Unknown',
          applicationType: appType,
          totalPartners: parseInt(totalPartnerCount) || (partners.length + 1),
          partnerDirectorDetails: partners,
          ddReviewStatus: ddReview.status ?? null,
          ddReviewMode: ddReview.mode ?? null,
          ddImageUrl: ddReview.ddImageUrl ?? app.ddDetails?.ddImageUrl ?? null
        });
      }
    } catch (err: any) {
      console.error('Error fetching application files:', err);
      setError(err.response?.data?.message || 'Failed to fetch files. Check network connection.');
    } finally {
      setLoading(false);
    }
  };
  
  // Format files data and load on component mount
  useEffect(() => {
    if (applicationId) {
      setSelectedApplicationId(applicationId);
      fetchFiles();
    } else {
      fetchApplications();
    }
  }, [applicationId]);
  
  // Fetch files when selectedApplicationId changes
  useEffect(() => {
    if (selectedApplicationId) {
      console.log('Selected application ID changed, fetching files for:', selectedApplicationId);
      fetchFiles();
    }
  }, [selectedApplicationId]);

  // Sort files alphabetically by fieldLabel
  const sortedFiles = [...files].sort((a, b) => 
    a.fieldLabel.localeCompare(b.fieldLabel)
  );

  // Categorize files into sections
  const categorizeFiles = (fileList: FileItem[]) => {
    const categories: {
      mainApplicant: FileItem[];
      partners: { [key: number]: FileItem[] };
      paymentDocuments: FileItem[];
      requestedDocuments: FileItem[];
    } = {
      mainApplicant: [],
      partners: {},
      paymentDocuments: [],
      requestedDocuments: []
    };

    fileList.forEach(file => {
      const fieldName = file.fieldName.toLowerCase();
      const filename = file.filename.toLowerCase();
      
      // Check for requested documents (approved via Document Request feature)
      if (fieldName.startsWith('requested_') || file.fieldLabel.toLowerCase().startsWith('requested document (approved)')) {
        categories.requestedDocuments.push(file);
      }
      // Check for payment documents
      else if (fieldName.includes('payment') || fieldName.includes('receipt') || 
          fieldName.includes('fullApplication') || fieldName.includes('full_application') ||
          filename.includes('payment') || filename.includes('receipt') ||
          filename.includes('full_application') || filename.includes('application_pdf')) {
        categories.paymentDocuments.push(file);
      }
      // Check for partner files (partner_0_, partner_1_, etc.)
      else if (fieldName.startsWith('partner_') || file.fieldLabel.toLowerCase().includes('partner')) {
        const match = fieldName.match(/partner[_]?(\d+)/i);
        if (match) {
          const partnerIndex = parseInt(match[1], 10);
          const partnerNumber = partnerIndex + 2; // partner_0 = Partner #2
          if (!categories.partners[partnerNumber]) {
            categories.partners[partnerNumber] = [];
          }
          categories.partners[partnerNumber].push(file);
        } else {
          // If can't determine partner number, add to first partner section
          if (!categories.partners[2]) {
            categories.partners[2] = [];
          }
          categories.partners[2].push(file);
        }
      }
      // Everything else is main applicant
      else {
        categories.mainApplicant.push(file);
      }
    });

    // Sort each category alphabetically
    categories.mainApplicant.sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel));
    categories.paymentDocuments.sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel));
    categories.requestedDocuments.sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel));
    Object.keys(categories.partners).forEach(key => {
      categories.partners[parseInt(key)].sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel));
    });

    return categories;
  };

  const categorizedFiles = categorizeFiles(files);
  const partnerNumbers = Object.keys(categorizedFiles.partners).map(Number).sort((a, b) => a - b);

  // Handle file preview
  const handlePreview = (previewUrl: string) => {
    // Get the URL path without domain
    let pathOnly = previewUrl;
    
    // Extract path if it's a full URL
    if (previewUrl.includes('://')) {
      // Get just the path portion
      const url = new URL(previewUrl);
      pathOnly = url.pathname;
    } else if (!previewUrl.startsWith('/')) {
      // Ensure path starts with /
      pathOnly = `/${previewUrl}`;
    }
    
    // Use the API_BASE from environment variable - ensures this works on any domain
    const correctUrl = `${API_BASE}${pathOnly}`;
    
    // Open in new tab
    window.open(correctUrl, '_blank');
  };

  // Handle single file download
  const handleDownload = (downloadUrl: string) => {
    // Get the URL path without domain
    let pathOnly = downloadUrl;
    
    // Extract path if it's a full URL
    if (downloadUrl.includes('://')) {
      // Get just the path portion
      const url = new URL(downloadUrl);
      pathOnly = url.pathname;
    } else if (!downloadUrl.startsWith('/')) {
      // Ensure path starts with /
      pathOnly = `/${downloadUrl}`;
    }
    
    // Use the API_BASE from environment variable - ensures this works on any domain
    const correctUrl = `${API_BASE}${pathOnly}`;
    
    window.location.href = correctUrl;
  };

  
  // Handle file replacement
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingField(fieldName);
    setUploadStatus(prev => ({ ...prev, [fieldName]: 'uploading' }));
    setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    setUploadMessages(prev => ({ ...prev, [fieldName]: 'Uploading...' }));
    
    uploadFormFileAdmin(selectedApplicationId, file, fieldName, (progressEvent) => {
      if (progressEvent.lengthComputable) {
        const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, [fieldName]: percentComplete }));
      }
    })
    .then(response => {
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'success' }));
      setUploadMessages(prev => ({ ...prev, [fieldName]: 'File replaced successfully!' }));
      
      // Update the file in the files array
      setFiles(prevFiles => prevFiles.map(file => {
        if (file.fieldName === fieldName) {
          return {
            ...file,
            filePath: response.filePath,
            filename: response.filePath.split('/').pop() || file.filename
          };
        }
        return file;
      }));
      
      // Clear the message after a delay (1 second)
      setTimeout(() => {
        setUploadMessages(prev => ({ ...prev, [fieldName]: '' }));
        setUploadStatus(prev => ({ ...prev, [fieldName]: 'idle' }));
        setUploadingField(null);
        
        // Refresh the files list to get updated URLs after upload
        if (selectedApplicationId) {
          fetchFiles();
        }
      }, 1000);
    })
    .catch(error => {
      console.error('Error replacing file:', error);
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessages(prev => ({ ...prev, [fieldName]: 'Failed to replace file. Please try again.' }));
      
      // Clear the error message after a longer delay
      setTimeout(() => {
        setUploadMessages(prev => ({ ...prev, [fieldName]: '' }));
        setUploadStatus(prev => ({ ...prev, [fieldName]: 'idle' }));
        setUploadingField(null);
      }, 5000);
    });
  };

  // If no specific application is selected, show a list of all available applications
  if (!selectedApplicationId) {
    const totalFilesAcrossApps = allApplications.reduce((sum, app) => sum + (app.fileCount || 0), 0);
    const totalBytesAcrossApps = allApplications.reduce((sum, app) => sum + (app.folderSize || 0), 0);
    const totalSizeLabel = formatTotalSize(totalBytesAcrossApps);

    return (
      <Container>
        <SearchContainer>
          <TitleContainer>
            {/* Updated to ensure correct total count is displayed */}
            <Title>Application Files ({totalApplications})</Title>
            <SummaryRow>
              <SummaryPill>
                <SummaryIcon>📁</SummaryIcon>
                <SummaryLabel>Total files</SummaryLabel>
                <SummaryValue>{totalFilesAcrossApps}</SummaryValue>
              </SummaryPill>
              <SummaryPill>
                <SummaryIcon>💾</SummaryIcon>
                <SummaryLabel>Total storage</SummaryLabel>
                <SummaryValue>{totalSizeLabel}</SummaryValue>
              </SummaryPill>
            </SummaryRow>
            <FilterContainer>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyWithFiles}
                    onChange={handleFileFilterChange}
                    color="primary"
                    size="small"
                  />
                }
                label="Applications with uploads"
                sx={{ '& .MuiTypography-root': { fontSize: '0.9rem', fontWeight: 500, color: '#4c6ef5' } }}
              />
            </FilterContainer>
          </TitleContainer>
          <SearchBox>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="search-field-label">Search by</InputLabel>
              <Select
                labelId="search-field-label"
                id="search-field"
                value={searchFilters.field}
                label="Search by"
                onChange={(e) => setSearchFilters(prev => ({ ...prev, field: e.target.value as SearchFilters['field'] }))}
                size="small"
              >
                <MenuItem value="all">All Fields</MenuItem>
                <MenuItem value="applicationId">Application ID</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder={`Search by ${searchFilters.field === 'all' ? 'any field' : searchFilters.field}...`}
              value={searchFilters.searchTerm}
              onChange={handleSearchChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchFilters.searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchFilters(prev => ({ ...prev, searchTerm: '' }))}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </SearchBox>
        </SearchContainer>
        {loading ? (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={30} />
              <div style={{ marginLeft: '10px' }}>Loading applications...</div>
            </div>
      {ddActionError && (
        <Alert severity="error" style={{ marginTop: '8px', marginBottom: '4px' }}>
          {ddActionError}
        </Alert>
      )}
      {ddActionSuccess && (
        <Alert severity="success" style={{ marginTop: '8px', marginBottom: '4px' }}>
          {ddActionSuccess}
        </Alert>
      )}
            
            {/* Show skeleton loader for better UX */}
            <FilesGrid>
              {Array.from(new Array(6)).map((_, index) => (
                <motion.div key={index} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                  <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: '12px', mb: 1 }} />
                </motion.div>
              ))}
            </FilesGrid>
          </div>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : applications.length === 0 ? (
          <NoFiles>
            <div>No applications found.</div>
          </NoFiles>
        ) : (
          <>
            <div style={{ marginBottom: '20px' }}>
              <p>Please select an application to view its files:</p>
              
              {/* Pagination control after instruction text */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handlePageChange} 
                    color="primary" 
                    showFirstButton 
                    showLastButton
                    size="medium"
                  />
                </div>
              )}
            </div>
            <FilesGrid>
              {getCurrentPageItems().map(app => {
                const rawStatus = app.status || '';
                const lowerStatus = rawStatus.toLowerCase();
                let statusLabel = '';
                let statusBg = 'rgba(148, 163, 184, 0.25)';
                let statusColor = '#0f172a';

                if (rawStatus) {
                  if (lowerStatus === 'draft') {
                    statusLabel = 'Draft';
                    statusBg = 'rgba(251, 191, 36, 0.2)';
                    statusColor = '#92400e';
                  } else if (lowerStatus === 'payment_complete') {
                    statusLabel = 'Payment Completed';
                    statusBg = 'rgba(16, 185, 129, 0.15)';
                    statusColor = '#047857';
                  } else if (lowerStatus === 'approved') {
                    statusLabel = 'Approved';
                    statusBg = 'rgba(34, 197, 94, 0.15)';
                    statusColor = '#166534';
                  } else if (lowerStatus === 'rejected') {
                    statusLabel = 'Rejected';
                    statusBg = 'rgba(239, 68, 68, 0.15)';
                    statusColor = '#b91c1c';
                  } else if (lowerStatus === 'submitted' || lowerStatus === 'under_review') {
                    statusLabel = lowerStatus === 'submitted' ? 'Submitted' : 'Under Review';
                    statusBg = 'rgba(59, 130, 246, 0.15)';
                    statusColor = '#1d4ed8';
                  } else {
                    statusLabel = rawStatus;
                  }
                }

                return (
                  <ClickableFileCard 
                    key={app._id} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => handleApplicationSelect(app.applicationId)}
                  >
                    {/* Application status chip on folder card */}
                    {statusLabel && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        left: '10px',
                        backgroundColor: statusBg,
                        color: statusColor,
                        borderRadius: '999px',
                        padding: '2px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 1px 3px rgba(15,23,42,0.25)'
                      }}>
                        <span style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '999px',
                          backgroundColor: statusColor,
                        }} />
                        <span>{statusLabel}</span>
                      </div>
                    )}

                    {/* Demand Draft badge placed near file count badge */}
                    {app.ddReviewStatus && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '40px',
                        backgroundColor: app.ddReviewStatus === 'pending_manual_review' ? '#f97316' : app.ddReviewStatus === 'approved' ? '#22c55e' : '#ef4444',
                        color: 'white',
                        borderRadius: '999px',
                        padding: '2px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
                      }}>
                        Demand Draft
                      </div>
                    )}

                    {/* File Count Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      backgroundColor: '#4c6ef5',
                      color: 'white',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                      border: '2px solid white',
                      zIndex: 1
                    }}>
                      {app.fileCount}
                    </div>
                    
                    <FileLabel style={{ color: '#5D4037' }}>
                      {app.fullName || app.formData?.nameInBlocks || 'Unknown Applicant'}
                    </FileLabel>
                    <FileName title={app.applicationId} style={{ color: '#6D4C41' }}>{app.applicationId}</FileName>
                    {app.phoneNumber && (
                      <div style={{ fontSize: '0.85rem', color: '#5D4037', marginBottom: '0.25rem' }}>
                        📱 {app.phoneNumber}
                      </div>
                    )}
                    <div style={{ fontSize: '0.875rem', color: '#6D4C41', marginBottom: '0.5rem' }}>
                      ✉️ {app.email}
                    </div>
                    
                    {/* Folder metadata info */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '0.75rem', 
                      color: '#8D6E63',
                      marginBottom: '0.5rem',
                      padding: '0.25rem 0',
                      borderTop: '1px dashed rgba(93, 64, 55, 0.2)'
                    }}>
                      <span title="Folder Size">📁 {app.folderSizeFormatted || '0 Bytes'}</span>
                      <span title="Number of Applicants">👥 {app.applicantCount || 1} {(app.applicantCount || 1) > 1 ? 'Applicants' : 'Applicant'}</span>
                    </div>
                    
                    {/* Indicator that the card is clickable */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#5D4037',
                      marginTop: 'auto',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid rgba(93, 64, 55, 0.2)',
                      fontWeight: 600,
                    }}>
                      <span style={{ marginRight: '6px' }}>View Files</span>
                      <FileDownloadIcon fontSize="small" />
                    </div>
                  </ClickableFileCard>
                );
              })}
            </FilesGrid>
            
            {/* Bottom Pagination */}
            {/* Always show pagination even if there's just one page */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                showFirstButton 
                showLastButton
              />
            </div>
          </>
        )}
      </Container>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <Container>
        <Title>Application Files</Title>
        <LoadingState>
          <CircularProgress size={40} color="primary" />
          <p>Loading files...</p>
        </LoadingState>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container>
        <Title>
          <StorageIcon style={{ marginRight: '0.5rem' }} />
          Application Files
        </Title>

        {error && (
          <Alert severity="error" style={{ marginBottom: '1rem' }}>
            {error}
          </Alert>
        )}      

        {!applicationId && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="application-select" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Select Application:
            </label>
            <select 
              id="application-select"
              value={selectedApplicationId}
              onChange={(e) => handleApplicationSelect(e.target.value)}
              style={{
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                width: '100%',
                maxWidth: '500px',
                fontSize: '1rem'
              }}
            >
              <option value="">-- Select an Application --</option>
              {applications.map(app => (
                <option key={app._id} value={app.applicationId}>
                  {app.applicationId} - {app.formData?.fullName || app.email}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <LoadingState>
            <CircularProgress size={40} />
            <p>Loading files...</p>
          </LoadingState>
        ) : !selectedApplicationId ? (
          <NoFiles>
            <StorageIcon style={{ fontSize: '3rem', opacity: 0.5 }} />
            <p>Please select an application to view files</p>
          </NoFiles>
        ) : files.length === 0 ? (
          <NoFiles>
            <StorageIcon style={{ fontSize: '3rem', opacity: 0.5 }} />
            <p>No files found for this application</p>
          </NoFiles>
        ) : (
          <FilesGrid>
            {sortedFiles.map((file, index) => (
              <FileCard 
                key={file.fieldName}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <FileLabel>
                  <span style={{ 
                    background: '#4c6ef5', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '24px', 
                    height: '24px', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    marginRight: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>{index + 1}</span>
                  {file.fieldLabel}
                </FileLabel>
                <FileName title={file.filename}>{file.filename}</FileName>
                
                {/* Display upload progress if file is being uploaded */}
                {uploadStatus[file.fieldName] === 'uploading' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      height: '6px',
                      width: '100%',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${uploadProgress[file.fieldName] || 0}%`,
                        backgroundColor: '#4c6ef5',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#64748b',
                      marginTop: '0.25rem'
                    }}>
                      {uploadProgress[file.fieldName] || 0}%
                    </div>
                  </div>
                )}
                
                {/* Display success/error message if any */}
                {uploadMessages[file.fieldName] && (
                  <div style={{
                    fontSize: '0.75rem',
                    color: uploadStatus[file.fieldName] === 'error' ? '#ef4444' : '#10b981',
                    marginBottom: '0.5rem'
                  }}>
                    {uploadMessages[file.fieldName]}
                  </div>
                )}
                
                <ButtonGroup>
                  <ActionButton 
                    onClick={() => handlePreviewFile(file)}
                    whileHover={{ scale: 1.05 }}
                    title="Preview file"
                    disabled={uploadStatus[file.fieldName] === 'uploading'}
                  >
                    <VisibilityIcon />
                  </ActionButton>
                  <ActionButton 
                    onClick={() => {
                      const path = file.downloadUrl.includes('/uploads/') ? 
                        `/uploads/${file.downloadUrl.split('/uploads/')[1]}` : 
                        file.downloadUrl;
                      const url = `${API_BASE}${path}`;
                      window.location.href = url;
                    }}
                    whileHover={{ scale: 1.05 }}
                    title="Download file"
                    disabled={uploadStatus[file.fieldName] === 'uploading'}
                  >
                    <DownloadIcon />
                  </ActionButton>
                  
                  {/* File upload input for replacement */}
                  <label htmlFor={`file-upload-${file.fieldName}`}>
                    <UploadInput
                      id={`file-upload-${file.fieldName}`}
                      type="file"
                      onChange={(e) => handleFileChange(e, file.fieldName)}
                      disabled={uploadStatus[file.fieldName] === 'uploading' || uploadingField !== null}
                    />
                    <ReplaceFileButton 
                      className={uploadStatus[file.fieldName] === 'uploading' || uploadingField !== null ? 'disabled' : ''}
                      title="Replace file"
                    >
                      <UploadFileIcon fontSize="small" />
                      <span>Replace file</span>
                    </ReplaceFileButton>
                  </label>
                </ButtonGroup>
              </FileCard>
            ))}
          </FilesGrid>
        )}

        {/* File Preview Modal — with zoom, pan, rotate, crop, save */}
        <FilePreviewModal
          isOpen={previewModal.isOpen}
          onClose={closePreviewModal}
          fileUrl={previewModal.url || null}
          fileName={previewModal.filename || null}
          fileType={previewModal.fileType || null}
          applicationId={selectedApplicationId || undefined}
          fieldName={previewModal.fieldName || undefined}
          onSaveSuccess={handlePreviewSaveSuccess}
          apiPrefix="/api/admin"
        />
      </Container>
    );
  }

  // Helper function to render a file card
  const renderFileCard = (file: FileItem, index: number, badgeColor: string = '#4c6ef5') => (
    <FileCard 
      key={file.fieldName}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <FileLabel>
        <span style={{ 
          background: badgeColor, 
          color: 'white', 
          borderRadius: '50%', 
          width: '24px', 
          height: '24px', 
          display: 'inline-flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          marginRight: '8px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}>{index + 1}</span>
        {file.fieldLabel}
      </FileLabel>
      <FileName title={file.filename}>{file.filename}</FileName>
      
      {uploadStatus[file.fieldName] === 'uploading' && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ height: '6px', width: '100%', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${uploadProgress[file.fieldName] || 0}%`, backgroundColor: badgeColor, transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>{uploadProgress[file.fieldName] || 0}%</div>
        </div>
      )}
      
      {uploadMessages[file.fieldName] && (
        <div style={{ fontSize: '0.75rem', color: uploadStatus[file.fieldName] === 'error' ? '#ef4444' : '#10b981', marginBottom: '0.5rem' }}>
          {uploadMessages[file.fieldName]}
        </div>
      )}
      
      <ButtonGroup>
        <ActionButton onClick={() => handlePreviewFile(file)} whileHover={{ scale: 1.05 }} title="Preview file" disabled={uploadStatus[file.fieldName] === 'uploading'}>
          <VisibilityIcon />
        </ActionButton>
        <ActionButton 
          onClick={() => {
            const path = file.downloadUrl.includes('/uploads/') ? `/uploads/${file.downloadUrl.split('/uploads/')[1]}` : file.downloadUrl;
            window.location.href = `${API_BASE}${path}`;
          }}
          whileHover={{ scale: 1.05 }} title="Download file" disabled={uploadStatus[file.fieldName] === 'uploading'}
        >
          <DownloadIcon />
        </ActionButton>
        <label htmlFor={`file-upload-${file.fieldName}`}>
          <UploadInput id={`file-upload-${file.fieldName}`} type="file" onChange={(e) => handleFileChange(e, file.fieldName)} disabled={uploadStatus[file.fieldName] === 'uploading' || uploadingField !== null} />
          <ReplaceFileButton className={uploadStatus[file.fieldName] === 'uploading' || uploadingField !== null ? 'disabled' : ''} title="Replace file">
            <UploadFileIcon fontSize="small" />
            <span>Replace file</span>
          </ReplaceFileButton>
        </label>
      </ButtonGroup>
    </FileCard>
  );

  // Helper to get partner name by index
  const getPartnerName = (partnerNum: number): string => {
    const partnerIndex = partnerNum - 2; // Partner #2 = index 0
    if (applicationDetails.partnerDirectorDetails && applicationDetails.partnerDirectorDetails[partnerIndex]) {
      const partner = applicationDetails.partnerDirectorDetails[partnerIndex];
      return partner.nameInBlocks || partner.name || 'Unknown';
    }
    return 'Unknown';
  };

  // Determine if partners should be called Directors based on application type
  const isDirectorType = ['Pvt. Ltd', 'Private Limited', 'Public Ltd', 'Public Limited', 'LLP'].some(
    type => applicationDetails.applicationType?.toLowerCase().includes(type.toLowerCase())
  );
  const partnerLabel = isDirectorType ? 'Director' : 'Partner';

  const handleDDReviewAction = async (action: 'approve' | 'reject') => {
    if (!selectedApplicationId) return;
    setDdActionLoading(true);
    setDdActionError(null);
    setDdActionSuccess(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/admin/applications/${selectedApplicationId}/dd-review`,
        { action },
        { withCredentials: true }
      );

      if (response.data.success) {
        const updatedStatus: string | null = response.data.ddReview?.status ?? response.data.application?.metadata?.ddReview?.status ?? null;
        const updatedAppStatus: string | undefined = response.data.application?.status;
        setDdActionSuccess(action === 'approve' ? 'Demand Draft approved successfully.' : 'Demand Draft rejected.');
        setApplicationDetails(prev => ({
          ...prev,
          ddReviewStatus: updatedStatus
        }));

        // Also update application list metadata for badges
        setAllApplications(prev => prev.map(app =>
          app.applicationId === selectedApplicationId
            ? { 
                ...app, 
                ddReviewStatus: updatedStatus,
                // Keep local status in sync with backend (e.g. payment_complete or rejected)
                status: updatedAppStatus || app.status
              }
            : app
        ));
        setApplications(prev => prev.map(app =>
          app.applicationId === selectedApplicationId
            ? { 
                ...app, 
                ddReviewStatus: updatedStatus,
                status: updatedAppStatus || app.status
              }
            : app
        ));

        // Refresh selected application's details and files from backend to avoid any stale state
        await fetchFiles();
      } else {
        setDdActionError(response.data.message || 'Failed to update DD review status');
      }
    } catch (error: any) {
      setDdActionError(error.response?.data?.message || 'Failed to update DD review status');
    } finally {
      setDdActionLoading(false);
    }
  };

  // Render files with categorized sections
  return (
    <Container>
      {/* Enhanced Title Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        color: 'white',
        padding: '12px 16px',
        borderRadius: '10px',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 600, background: 'rgba(255,255,255,0.15)', padding: '4px 10px', borderRadius: '6px' }}>
            🆔 {selectedApplicationId}
          </span>
          <span style={{ fontSize: '0.95rem' }}>📁 Files: {files.length}</span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ fontSize: '0.95rem', color: '#fbbf24' }}>👥 Total {partnerLabel}s: {applicationDetails.totalPartners}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '5px 12px',
            borderRadius: '16px',
            fontSize: '0.85rem',
            fontWeight: 500
          }}>
            {applicationDetails.applicationType || 'Individual'}
          </div>
          {applicationDetails.ddReviewStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{
                background: applicationDetails.ddReviewStatus === 'pending_manual_review' ? 'rgba(251, 191, 36, 0.85)' : applicationDetails.ddReviewStatus === 'approved' ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
                color: '#111827',
                padding: '4px 10px',
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 600
              }}>
                DD: {applicationDetails.ddReviewStatus === 'pending_manual_review' ? 'Pending Manual Review' : applicationDetails.ddReviewStatus === 'approved' ? 'Approved' : 'Rejected'}
              </span>
              {applicationDetails.ddReviewStatus === 'pending_manual_review' && (
                <>
                  <button
                    onClick={() => handleDDReviewAction('approve')}
                    disabled={ddActionLoading}
                    style={{
                      background: '#22c55e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: ddActionLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {ddActionLoading ? 'Saving...' : 'Accept DD'}
                  </button>
                  <button
                    onClick={() => handleDDReviewAction('reject')}
                    disabled={ddActionLoading}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: ddActionLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {ddActionLoading ? 'Saving...' : 'Reject DD'}
                  </button>
                </>
              )}
            </div>
          )}
          {ddActionSuccess && (
            <Alert severity="success" style={{ fontSize: '0.8rem', padding: '4px 10px', borderRadius: '999px', fontWeight: 500 }}>
              {ddActionSuccess}
            </Alert>
          )}
          {ddActionError && (
            <Alert severity="error" style={{ fontSize: '0.8rem', padding: '4px 10px', borderRadius: '999px', fontWeight: 500 }}>
              {ddActionError}
            </Alert>
          )}
          <button
            onClick={handleFindOrphanedFiles}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            title="Find and remove orphaned files from this user's folder"
          >
            <DeleteSweepIcon style={{ fontSize: '1rem' }} />
            Cleanup Folder
          </button>
        </div>
      </div>
      
      {/* Cleanup Modal */}
      {cleanupModal.isOpen && (
        <>
          <div 
            onClick={closeCleanupModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1000
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            zIndex: 1001,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.25rem' }}>
                🧹 Cleanup Orphaned Files
              </h2>
              <button 
                onClick={closeCleanupModal}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}
              >
                ×
              </button>
            </div>
            
            {cleanupModal.loading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <CircularProgress size={40} />
                <p style={{ marginTop: '16px', color: '#64748b' }}>Scanning for orphaned files...</p>
              </div>
            )}
            
            {cleanupModal.error && (
              <Alert severity="error" style={{ marginBottom: '16px' }}>
                {cleanupModal.error}
              </Alert>
            )}
            
            {cleanupModal.success && (
              <Alert severity="success" style={{ marginBottom: '16px' }}>
                {cleanupModal.success}
              </Alert>
            )}
            
            {!cleanupModal.loading && !cleanupModal.success && cleanupModal.orphanedFiles.length === 0 && !cleanupModal.error && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#10b981' }}>
                <span style={{ fontSize: '3rem' }}>✅</span>
                <p style={{ marginTop: '16px', fontWeight: 500 }}>No orphaned files found!</p>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>This folder is clean.</p>
              </div>
            )}
            
            {!cleanupModal.loading && cleanupModal.orphanedFiles.length > 0 && (
              <>
                <Alert severity="warning" style={{ marginBottom: '16px' }}>
                  Found <strong>{cleanupModal.orphanedFiles.length}</strong> orphaned files not linked to this application.
                  These files will be permanently deleted.
                </Alert>
                
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px',
                  maxHeight: '250px',
                  overflow: 'auto',
                  marginBottom: '20px'
                }}>
                  {cleanupModal.orphanedFiles.map((file, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '8px 12px',
                        borderBottom: index < cleanupModal.orphanedFiles.length - 1 ? '1px solid #e2e8f0' : 'none',
                        fontSize: '0.85rem',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ color: '#ef4444' }}>🗑️</span>
                      {file}
                    </div>
                  ))}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeCleanupModal}
                    style={{
                      background: '#f1f5f9',
                      color: '#64748b',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteOrphanedFiles}
                    disabled={cleanupModal.deleting}
                    style={{
                      background: cleanupModal.deleting ? '#fca5a5' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontWeight: 500,
                      cursor: cleanupModal.deleting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {cleanupModal.deleting ? (
                      <>
                        <CircularProgress size={16} style={{ color: 'white' }} />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <DeleteSweepIcon style={{ fontSize: '1rem' }} />
                        Delete {cleanupModal.orphanedFiles.length} Files
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      
      {/* Main Applicant Section */}
      {categorizedFiles.mainApplicant.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #4c6ef5 0%, #667eea 100%)',
            color: 'white',
            padding: '10px 16px',
            borderRadius: '8px 8px 0 0',
            fontWeight: 600,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>👤</span>
            <span>Main Applicant - {applicationDetails.applicantName}</span>
            <span style={{ opacity: 0.8, fontWeight: 400, fontSize: '0.9rem' }}>({categorizedFiles.mainApplicant.length} files)</span>
          </div>
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '0 0 8px 8px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
            <FilesGrid>
              {categorizedFiles.mainApplicant.map((file, index) => renderFileCard(file, index, '#4c6ef5'))}
            </FilesGrid>
          </div>
        </div>
      )}

      {/* Partner/Director Sections */}
      {partnerNumbers.map(partnerNum => (
        <div key={`partner-${partnerNum}`} style={{ marginBottom: '1.5rem' }}>
          <div style={{
            background: `linear-gradient(135deg, ${partnerNum % 2 === 0 ? '#f59e0b' : '#10b981'} 0%, ${partnerNum % 2 === 0 ? '#d97706' : '#059669'} 100%)`,
            color: 'white',
            padding: '10px 16px',
            borderRadius: '8px 8px 0 0',
            fontWeight: 600,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>👥</span>
            <span>{partnerLabel} #{partnerNum} - {getPartnerName(partnerNum)}</span>
            <span style={{ opacity: 0.8, fontWeight: 400, fontSize: '0.9rem' }}>({categorizedFiles.partners[partnerNum].length} files)</span>
          </div>
          <div style={{ background: partnerNum % 2 === 0 ? '#fffbeb' : '#f0fdf4', padding: '12px', borderRadius: '0 0 8px 8px', border: `1px solid ${partnerNum % 2 === 0 ? '#fde68a' : '#bbf7d0'}`, borderTop: 'none' }}>
            <FilesGrid>
              {categorizedFiles.partners[partnerNum].map((file, index) => renderFileCard(file, index, partnerNum % 2 === 0 ? '#f59e0b' : '#10b981'))}
            </FilesGrid>
          </div>
        </div>
      ))}

      {/* Payment Documents Section */}
      {categorizedFiles.paymentDocuments.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '10px 10px 0 0',
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '1.2rem' }}>💳</span>
            Payment Documents ({categorizedFiles.paymentDocuments.length})
          </div>
          <div style={{ background: '#faf5ff', padding: '16px', borderRadius: '0 0 10px 10px', border: '1px solid #e9d5ff', borderTop: 'none' }}>
            <FilesGrid>
              {categorizedFiles.paymentDocuments.map((file, index) => renderFileCard(file, index, '#8b5cf6'))}
            </FilesGrid>
          </div>
        </div>
      )}

      {/* Requested Documents (approved) Section */}
      {categorizedFiles.requestedDocuments.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 50%, #4f46e5 100%)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '10px 10px 0 0',
            fontWeight: 600,
            fontSize: '1.05rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '1.2rem' }}>📧</span>
            <span>Requested Document (approved)</span>
            <span style={{ opacity: 0.85, fontWeight: 400, fontSize: '0.9rem' }}>({categorizedFiles.requestedDocuments.length} file{categorizedFiles.requestedDocuments.length > 1 ? 's' : ''})</span>
          </div>
          <div style={{
            background: '#eff6ff',
            padding: '16px',
            borderRadius: '0 0 10px 10px',
            border: '1px solid #bfdbfe',
            borderTop: 'none'
          }}>
            <FilesGrid>
              {categorizedFiles.requestedDocuments.map((file, index) => renderFileCard(file, index, '#0ea5e9'))}
            </FilesGrid>
          </div>
        </div>
      )}
      
      {files.length > 1 && (
        <ZipDownloadButton 
          onClick={handleDownloadAllAsZip}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FileDownloadIcon /> Download All Files
        </ZipDownloadButton>
      )}

      {/* File Preview Modal — with zoom, pan, rotate, crop, save */}
      <FilePreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreviewModal}
        fileUrl={previewModal.url || null}
        fileName={previewModal.filename || null}
        fileType={previewModal.fileType || null}
        applicationId={selectedApplicationId || undefined}
        fieldName={previewModal.fieldName || undefined}
        onSaveSuccess={handlePreviewSaveSuccess}
      />
    </Container>
  );
};

export default AdminApplicationFiles;
